<template>
  <div class="column forecast-history-column">
    <div class="field has-addons has-addons-centered forecast-tabs-desktop">
      <p class="control">
        <button
          class="button is-medium forecast-tab"
          :class="{
            'is-primary': !contributionChartActive,
            'is-inactive': contributionChartActive && isDark,
          }"
          @click="contributionChartActive = false"
        >
          <span class="button-label"> Forecast Impacts </span>
          <ncl-info-icon
            location="bottom"
            text="The forecast impacts graph shows, for each day, the day-to-day changes in the contributions of the pooled forecasts from the different variable categories. The sum of all impacts equals the day-to-day change of the overall pooled forecast."
            :secondaryColor="!contributionChartActive"
          />
        </button>
      </p>

      <p class="control">
        <v-tooltip
          location="bottom"
          text="The selected model does not calculate forecast contributions."
          v-if="!contributionsAvailable"
          activator="#contributions-button"
        />
        <button
          :disabled="!contributionsAvailable"
          id="contributions-button"
          class="button is-medium forecast-tab"
          :class="{
            'is-primary': contributionChartActive,
            'is-inactive': !contributionChartActive && isDark,
          }"
          @click="contributionChartActive = true"
        >
          <span class="button-label"> Forecast Contributions </span>
          <ncl-info-icon
            location="bottom"
            text="The forecast contributions chart shows, for each day, the contributions of the pooled forecasts stemming from different variable categories to the overall pooled forecast. The sum of all contributions equals the overall pooled forecast. The About site explains how these contributions are calculated."
            :secondaryColor="contributionChartActive"
          />
        </button>
      </p>
    </div>
    <div class="forecast-tabs-mobile">
      <v-select
        v-model="contributionChartActive"
        :items="tabSelectItems"
        :item-props="tabItemProps"
        density="compact"
        variant="outlined"
        hide-details
        menu-icon="mdi-chevron-down"
        class="forecast-tab-select"
      ></v-select>
    </div>
    <highcharts
      v-if="maxTime > 0 && !isLoadingData && getOutputData.length > 0"
      :constructorType="'stockChart'"
      :options="chartOptions"
      ref="testChart"
      class="forecast-history-chart"
    ></highcharts>
    <div
      v-else-if="isLoadingData"
      class="forecast-history-chart forecast-history-loading"
    >
      <p>Loading chart...</p>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash-es";
import { mapActions, mapState } from "pinia";
import { useDetailStore } from "@/stores/detail";
import NclInfoIcon from "@/components/NclInfoIcon.vue";
import { useHighchartsTheme } from "@/composables/useHighchartsTheme";

export default {
  components: { NclInfoIcon },
  setup() {
    const { isDark, getHighchartsTheme } = useHighchartsTheme();
    return { isDark, getHighchartsTheme };
  },
  props: {
    country: String,
    targetperiod: String,
    targetvariable: String,
    indicator: { type: String, default: "en" },
    model: String,
  },
  data() {
    return {
      inactiveCategories: [],
      contributionChartActive: false,
      clickedDayTime: null,
      hoveredDayTime: null,
      chart: null,
      isLoadingData: false,
      isDestroyed: false,
    };
  },
  computed: {
    ...mapState(useDetailStore, ["getOutputData", "getMaxTimestamp"]),
    tabSelectItems() {
      return [
        { title: "Forecast Impacts", value: false, disabled: false },
        {
          title: "Forecast Contributions",
          value: true,
          disabled: !this.contributionsAvailable,
        },
      ];
    },
    // Call getters with model prop to ensure correct filtering
    getForecastContributionsData() {
      const detailStore = useDetailStore();
      return detailStore.getForecastContributionsData(this.model);
    },
    getForecastImpactData() {
      const detailStore = useDetailStore();
      return detailStore.getForecastImpactData(this.model);
    },
    getCategories() {
      const detailStore = useDetailStore();
      return detailStore.getCategories(this.model);
    },
    activeDayTime() {
      if (this.clickedDayTime) {
        return this.clickedDayTime;
      }
      return this.hoveredDayTime;
    },
    maxTime() {
      let max = !this.getMaxTimestamp ? 0 : this.getMaxTimestamp;
      max = Math.max(0, max);
      return max;
    },
    activeBarChartData() {
      if (this.contributionChartActive) {
        return this.getForecastContributionsData;
      }
      return this.getForecastImpactData;
    },
    chartOptions() {
      let xAxisName = "";
      if (this.targetvariable === "gdp") {
        if (this.contributionChartActive) {
          xAxisName = "Contributions to q-o-q growth in pp";
        } else {
          xAxisName = "Change in contr. to q-o-q growth in pp";
        }
      }

      let axisTitle = "Q-o-q growth in %";

      // Padding the data with null values so get "whitespace" after the chart where there might not be data.
      // All timestamps are midnight UTC (from parseDate), so we use plain ms arithmetic
      // to avoid startOfDay/addDays shifting dates across day boundaries in local timezones.
      const DAY_MS = 24 * 60 * 60 * 1000;
      const lastDataX =
        this.getOutputData[this.getOutputData.length - 1]?.x ?? Date.now();
      let paddedDates = [];
      if (this.getOutputData.length) {
        for (
          let paddingX = lastDataX + DAY_MS;
          paddingX < this.maxTime;
          paddingX += DAY_MS
        ) {
          paddedDates.push({ selected: false, x: paddingX, y: null });
        }
      }

      let series1 = {
        showInLegend: false,
        name:
          this.country == "ie" ? "MDD growth forecast" : "GDP growth forecast",
        data: [...(this.getOutputData || []), ...paddedDates].sort(
          (a, b) => a.x - b.x,
        ),
        showInNavigator: true,
        turboThreshold: 0,
        dataGrouping: { enabled: false },
        yAxis: 0,
        marker: {
          enabled: true,
          radius: 0.1,
          states: { hover: { radius: 7 } },
        },
      };

      // Separate scatter series for the orange selection dot.
      // Keeping it out of series1.data means hover events (which change activeDayTime
      // but not clickedDayTime) never trigger a chart.update() redraw — the dot is stable.
      const selectedRawPoint = (this.getOutputData || []).find(
        (p) => p.x === this.clickedDayTime && p.y != null,
      );
      const selectedDotSeries = {
        type: "scatter",
        name: "selected-marker",
        showInLegend: false,
        showInNavigator: false,
        enableMouseTracking: true,
        yAxis: 0,
        zIndex: 10,
        data: selectedRawPoint
          ? [{ x: selectedRawPoint.x, y: selectedRawPoint.y }]
          : [],
        marker: {
          enabled: true,
          fillColor: "orange",
          radius: 7,
          lineWidth: 0.5,
          symbol: "circle",
          states: { hover: { fillColor: "orange", radius: 8 } },
        },
        tooltip: { pointFormat: "<b>{point.y:.2f}%</b>" },
      };

      const theme = this.getHighchartsTheme(this.isDark);

      return {
        ...theme,
        title: undefined,
        legend: {
          ...theme.legend,
          enabled: true,
        },
        chart: {
          ...theme.chart,
          marginLeft: 100,
          spacingTop: 10,
          animation: false,
          // The two y-axes are unrelated (% growth vs pp impacts). With
          // alignTicks (default true) Highcharts reconciles tick counts across
          // them; in narrow zooms (1m) yAxis[0]'s minRange and yAxis[1]'s
          // minTickInterval conflict and tick generation explodes, freezing
          // the tab in setTickPositions.
          alignTicks: false,
          events: {
            load: (function (self) {
              return function () {
                // Clean up old references
                if (self.chart) {
                  self.chart = null;
                }
                if (self.naLabel) {
                  self.naLabel = null;
                }

                self.chart = this; // saving chart reference in the component

                // The chart only mounts when getOutputData.length > 0 (v-if guarantee),
                // so this is the earliest safe moment to initialize the selection.
                if (!self.clickedDayTime) {
                  const validPoints = (self.getOutputData || []).filter(
                    (p) => p.y != null && !isNaN(p.y),
                  );
                  if (validPoints.length > 0) {
                    const lastX = validPoints[validPoints.length - 1].x;
                    self.clickedDayTime = lastX;
                    self.$emit("activedaytimechange", { x: lastX });
                  }
                }

                // saving N/A text in the middle of the bottom (bar) chart
                // to be able to set its opacity based on availability of bar data
                self.naLabel = this.renderer
                  .label("N/A", this.chartWidth / 2, 400)
                  .add()
                  .css({
                    color: self.isDark ? "#A0A0A0" : "gray",
                    fontWeight: 600,
                    fontSize: 50,
                    opacity: "0%",
                  });
              };
            })(this),
            render: (function (self) {
              return function () {
                // Bar highlight lives on the SVG elements (see _updateBarClasses)
                // and is wiped whenever series re-render — reapply after each redraw.
                self._updateBarClasses(self.activeDayTime, this);

                const rs = this.rangeSelector;

                // Never let `selected` persist into rangeSelector options:
                // redrawElements() re-fires clickButton(options.selected) on every
                // highcharts-vue chart.update(), snapping extremes back (e.g. to
                // "All" after the user zoomed elsewhere) and bypassing the custom
                // 1m handler. Instance state (rs.selected) is untouched, so button
                // highlight / dropdown checkmark still work — updateButtonStates()
                // re-derives them from the visible range on each redraw.
                if (rs?.options) rs.options.selected = void 0;

                // Highcharts bug (present in 12.x and 13.0): redrawElements()
                // removes dropdown options at i+1 but createButton() re-inserts at
                // i+2, so after any chart.update the <option> order flips
                // ([Zoom, All, 1m]) while the internal buttons array stays
                // [1m, All] — the dropdown then fires the wrong button and
                // checkmarks the wrong row. Only textContent is scrambled
                // (selectedIndex/disabled are index-based), so rewrite it per index.
                if (rs?.dropdown) {
                  rs.buttonOptions.forEach((opt, i) => {
                    const option = rs.dropdown.options[i + 1];
                    const text = opt.title || opt.text;
                    if (option && option.textContent !== text) {
                      option.textContent = text;
                    }
                  });
                }

                // On mobile, swap rangeSelector text inputs to native date inputs
                // so tap opens the OS calendar picker. Highcharts re-renders these
                // on every redraw, so reapply each time.
                if (window.innerWidth > 768) return;
                const inputs = this.container?.parentNode?.querySelectorAll(
                  "input.highcharts-range-selector",
                );
                inputs?.forEach((i) => {
                  if (i.type !== "date") i.type = "date";
                });
              };
            })(this),
          },
        },
        xAxis: {
          ...theme.xAxis,
          type: "datetime",
          title: {
            ...theme.xAxis.title,
            text: "Date of forecast",
          },
          dateTimeLabelFormats: {
            month: "%b %Y",
            year: "%b %Y",
          },
          max: this.maxTime,
          events: {
            afterSetExtremes: debounce(() => {
              this.syncYAxis();
            }, 20),
          },
        },
        yAxis: [
          {
            ...theme.yAxis,
            title: {
              ...theme.yAxis.title,
              reserveSpace: false,
              text: axisTitle,
              x: -3,
            },
            height: "40%",
            labels: {
              ...theme.yAxis.labels,
              enabled: true,
              x: 1,
              y: 5,
              format: "{value:.2f} ",
            },
            opposite: false,
            minRange: !this.contributionChartActive ? 0.2 : undefined,
            showLastLabel: true,
          },
          {
            ...theme.yAxis,
            title: {
              ...theme.yAxis.title,
              reserveSpace: false,
              text: xAxisName,
              x: -3,
            },
            top: "60%",
            height: "40%",
            labels: {
              ...theme.yAxis.labels,
              enabled: true,
              x: 30,
              y: 5,
              format: "{value:.2f}",
            },
            minTickInterval: 0.01,
            opposite: false,
            showLastLabel: true,
          },
        ],
        series: [
          series1,
          selectedDotSeries,
          ...(this.activeBarChartData || []),
        ],
        plotOptions: {
          ...theme.plotOptions,
          column: {
            stacking: "normal",
            tooltip: {
              shared: true,
              valueDecimals: 2,
              valueSuffix: "pp",
            },
          },
          series: {
            ...theme.plotOptions?.series,
            allowPointSelect: false,
            cursor: "pointer",
            point: {
              events: {
                click: (day) => {
                  if (day.point.y == null) return;
                  // Only respond to clicks on the main forecast line or the selection dot.
                  // Ignore clicks on bar chart columns (impact / contribution series).
                  if (day.point.series.type === "column") return;
                  this.clickedDayTime = day.point.x;
                },
                mouseOver: (day) => {
                  if (day.target.y == null) return;
                  this.hoveredDayTime = day.target.x;
                },
                mouseOut: () => {
                  this.hoveredDayTime = null;
                },
              },
            },
            events: {
              legendItemClick: (item) => {
                const name = item.target.name;
                const color = item.target.color;
                const isActive = item.target.visible;

                if (isActive) {
                  this.inactiveCategories = this.inactiveCategories.filter(
                    (o) => o.name !== item.target.name,
                  );
                } else {
                  this.inactiveCategories = [
                    ...this.inactiveCategories,
                    {
                      name: name,
                      color: color,
                    },
                  ];
                }
                this.$emit("activecategorieschange", this.activeCategories);
              },
            },
            tooltip: {
              valueDecimals: 2,
              valueSuffix: "%",
            },
            animation: false,
          },
        },
        rangeSelector: {
          ...theme.rangeSelector,
          dropdown: "responsive",
          inputDateFormat: "%d.%m.%Y",
          inputEditDateFormat: "%d.%m.%Y",
          inputPosition: {
            align: "right",
            x: 0,
            y: 0,
          },
          buttonPosition: {
            align: "left",
            x: 0,
            y: 0,
          },
          buttons: [
            {
              type: "month",
              count: 1,
              text: "1m",
              title: "View 1 month",
              events: {
                click: (function (self) {
                  return function () {
                    if (self.getOutputData.length) {
                      let lastDate =
                        self.getOutputData[self.getOutputData.length - 1].x;
                      let oneMonthBefore = lastDate - 30 * 24 * 60 * 60 * 1000;
                      const chart = self.chart || self.$refs.testChart?.chart;
                      const xAxis = chart?.xAxis?.[0];
                      if (xAxis) {
                        // Re-applying identical extremes (clicking 1m while
                        // already in 1m view) sends Highcharts into a redraw
                        // loop that freezes the tab — skip when nothing changes.
                        const { min, max } = xAxis.getExtremes();
                        if (min !== oneMonthBefore || max !== lastDate) {
                          xAxis.setExtremes(oneMonthBefore, lastDate);
                        }
                      }
                      // Do NOT call rangeSelector.setSelected(0) here: it persists
                      // `selected` into the chart options, and every highcharts-vue
                      // chart.update() then re-fires clickButton(selected) in
                      // redrawElements(), cascading setExtremes -> redraw until the
                      // tab freezes.
                      // The updateButtonStates() pass that ran during setExtremes
                      // may have carried a stale `selected` (persisted by a prior
                      // default "All" click), which makes it skip the matching 1m
                      // button and fall back to the "Zoom" label. That pass also
                      // cleared the stale value, so a second pass now marks 1m
                      // correctly. It re-persists options.selected — clear it so
                      // redrawElements() can't re-fire clickButton on the next
                      // chart.update (the render handler also clears it each redraw).
                      const rs = chart?.rangeSelector;
                      if (rs) {
                        rs.updateButtonStates();
                        rs.options.selected = void 0;
                      }
                    }
                    return false;
                  };
                })(this),
              },
            },
            {
              type: "all",
              text: "All",
              title: "View all",
            },
          ],
        },
        navigator: {
          ...theme.navigator,
          enabled: true,
          adaptToUpdatedData: false,
        },
        scrollbar: {
          ...theme.scrollbar,
          enabled: true,
        },
        responsive: {
          rules: [
            {
              condition: { maxWidth: 768 },
              chartOptions: {
                rangeSelector: {
                  dropdown: "always",
                  inputEnabled: true,
                  inputDateFormat: "%Y-%m-%d",
                  inputEditDateFormat: "%Y-%m-%d",
                  inputPosition: { align: "right", x: 0, y: 0 },
                  buttonPosition: { align: "left", x: 0, y: 0 },
                },
                chart: { marginLeft: 60 },
              },
            },
          ],
        },
      };
    },
    activeCategories() {
      if (this.getCategories) {
        return this.getCategories.filter(
          (obj) => !this.inactiveCategories.some((x) => x.name === obj.name),
        );
      }
      return [];
    },
    contribNotApplicableLabel() {
      switch (this.model) {
        case "dfm":
        case "dfm_Q419":
        case "dfm_excl_covid":
          return "By construction, the dynamic factor model does not generate forecast contributions but forecast impacts.";
        case "mf-esn":
          return "Currently, the multi-frequency echo state network models do not generate forecast contributions.";
        case "all":
          return "By construction, the dynamic factor model does not generate forecast contributions. As a consequence, the pooled forecast, which results from pooling the forecasts from all model classes, does not come with forecast contributions neither.";
        default:
          return "";
      }
    },
    contributionsAvailable() {
      return (
        [
          "all",
          "dfm",
          "dfm_Q419",
          "dfm_excl_covid",
          "mf-esn",
          "randomwalk",
          "rollmean",
          "ar_iterative_bic",
          "arima",
          "ar_direct_bic",
        ].indexOf(this.model) < 0
      );
    },
  },
  watch: {
    activeDayTime: debounce(function (newActiveDayTime) {
      if (!newActiveDayTime) {
        return;
      }
      this.$emit("activedaytimechange", {
        x: newActiveDayTime,
      });
      this._updateBarClasses(newActiveDayTime);
    }, 200),
    hoveredDayTime(newHoveredDayTime) {
      this._updateBarClasses(newHoveredDayTime || this.clickedDayTime);
    },
    clickedDayTime() {
      // Use $nextTick so chart.update() from vue-highcharts (reactive chartOptions) runs
      // before we call setData(), avoiding a stale-series-reference conflict.
      this.$nextTick(() => this._updateSelectedDot());
    },
    activeBarChartData(newData) {
      if (!this.isLoadingData) {
        this.syncYAxis();
        if (this.naLabel && newData) {
          if (newData.length > 0) {
            this.naLabel.attr({ style: "opacity: 0%" });
          } else {
            this.naLabel.attr({ style: "opacity: 30%" });
          }
        }
      }
    },

    // Note: We rely on the component key to trigger recreation when props change.
    // This prevents race conditions where old and new component instances both
    // try to load data and the last request wins (potentially with old data).
    // When the key changes, the old component is destroyed and only the new one loads data.
  },
  mounted() {
    // Reset contribution view if not available for this model
    if (this.contributionChartActive && !this.contributionsAvailable) {
      this.contributionChartActive = false;
    }
    this.loadData();
  },
  beforeUpdate() {
    // Null out stale hover refs before Vue triggers a chart update.
    // Avoids "this.series is undefined" in Highcharts pointer.reset() when
    // series are destroyed/recreated. We do NOT call pointer.reset() here
    // because it fires our reactive mouseOut handler and causes cascading updates.
    const chart = this.chart || this.$refs.testChart?.chart;
    if (chart) {
      chart.hoverPoint = null;
      chart.hoverSeries = null;
      chart.hoverPoints = null;
    }
  },
  beforeUnmount() {
    // Mark as destroyed to prevent debounced operations
    this.isDestroyed = true;

    // Clean up chart references to prevent memory leaks
    if (this.chart) {
      this.chart = null;
    }
    if (this.naLabel) {
      this.naLabel = null;
    }
    // Reset loading state
    this.isLoadingData = false;
  },
  methods: {
    ...mapActions(useDetailStore, [
      "loadOutputData",
      "loadImpactData",
      "loadContributionData",
    ]),
    tabItemProps(item) {
      return { title: item.title, disabled: item.disabled };
    },
    // Debounced version to prevent multiple simultaneous loads
    debouncedLoadData: debounce(function () {
      if (!this.isDestroyed) {
        this.loadData();
      }
    }, 50),
    syncYAxis() {
      // Don't sync during loading to avoid chart inconsistencies
      if (this.isLoadingData) {
        return;
      }

      // Get chart from ref or direct reference
      const chart = this.chart || this.$refs.testChart?.chart;

      if (!chart || !chart.yAxis || !chart.yAxis[0] || !chart.yAxis[1]) {
        return;
      }

      // Ensure chart series are properly initialized
      if (!chart.series || chart.series.length === 0) {
        return;
      }

      try {
        if (this.contributionChartActive) {
          const extremes0 = chart.yAxis[0].getExtremes();
          const extremes1 = chart.yAxis[1].getExtremes();

          if (!extremes0 || !extremes1) {
            return;
          }

          const min = Math.min(extremes0.dataMin, extremes1.dataMin);
          const max = Math.max(extremes0.dataMax, extremes1.dataMax);

          // Skip when already synced: setExtremes triggers a redraw, which can
          // re-enter this handler via afterSetExtremes and freeze the tab.
          // Compare against userMin/userMax (what setExtremes stores), not
          // min/max, which Highcharts may tick-adjust.
          const EPS = 1e-9;
          const synced = (ex) =>
            ex.userMin !== undefined &&
            ex.userMax !== undefined &&
            Math.abs(ex.userMin - min) < EPS &&
            Math.abs(ex.userMax - max) < EPS;
          const alreadySynced = synced(extremes0) && synced(extremes1);

          if (isFinite(min) && isFinite(max) && !alreadySynced) {
            chart.yAxis[0].setExtremes(min, max);
            chart.yAxis[1].setExtremes(min, max);
          }
        } else {
          // No syncing, just use highcharts values
          // Calling setExtremes with no parameters seems to let Highcharts auto calculate axis.
          // Skip when both axes are already on auto extremes — calling
          // setExtremes() again would just trigger redraws (same re-entry risk).
          if (
            chart.yAxis[0].userMin !== undefined ||
            chart.yAxis[0].userMax !== undefined
          ) {
            chart.yAxis[0].setExtremes();
          }
          if (
            chart.yAxis[1].userMin !== undefined ||
            chart.yAxis[1].userMax !== undefined
          ) {
            chart.yAxis[1].setExtremes();
          }
        }
      } catch (error) {
        console.warn("Error syncing Y axes:", error);
      }
    },
    _updateSelectedDot() {
      const chart = this.chart || this.$refs.testChart?.chart;

      if (!chart || !chart.series) {
        console.warn("[dot] no chart or series — returning early");
        return;
      }
      const scatterSeries = chart.series.find(
        (s) => s.name === "selected-marker",
      );
      if (!scatterSeries) {
        console.warn(
          "[dot] scatter series not found. Series names:",
          chart.series.map((s) => s.name),
        );
        return;
      }
      // Null pointer state referencing the scatter series before destroying
      // its points; otherwise pointer.reset / runPointActions on subsequent
      // mouse / zoom events deref a destroyed point and throw.
      if (chart.hoverPoint && chart.hoverPoint.series === scatterSeries) {
        chart.hoverPoint = null;
      }
      if (chart.hoverSeries === scatterSeries) {
        chart.hoverSeries = null;
      }
      if (chart.hoverPoints) {
        chart.hoverPoints = chart.hoverPoints.filter(
          (p) => p.series !== scatterSeries,
        );
        if (chart.hoverPoints.length === 0) chart.hoverPoints = null;
      }
      if (chart.tooltip) chart.tooltip.hide(0);

      const point = (this.getOutputData || []).find(
        (p) => p.x === this.clickedDayTime && p.y != null && !isNaN(p.y),
      );
      scatterSeries.setData(
        point ? [{ x: point.x, y: point.y }] : [],
        true, // redraw
        false, // animation
        false, // updatePoints
      );
    },
    // Applies bar dim/highlight directly on the rendered SVG elements.
    // Must NOT mutate the reactive store data: those objects sit inside
    // chartOptions.series and highcharts-vue deep-watches options, so a
    // className write there triggers a full chart.update() per mouse move —
    // destroying series mid-hover and crashing/freezing the pointer.
    _updateBarClasses(day, chart = null) {
      if (!day) return;
      chart = chart || this.chart || this.$refs.testChart?.chart;
      if (!chart?.series) return;
      chart.series.forEach((serie) => {
        if (serie.type !== "column") return;
        (serie.points || []).forEach((p) => {
          p.graphic
            ?.removeClass("bar-active")
            .removeClass("bar-inactive")
            .addClass(p.x == day ? "bar-active" : "bar-inactive");
        });
      });
    },
    async loadData() {
      if (this.isDestroyed) {
        return;
      }

      this.isLoadingData = true;
      try {
        const impactPromise = this.loadImpactData({
          country: this.country,
          targetVariable: this.targetvariable,
          targetPeriod: this.targetperiod,
          model: this.model,
        });
        const contributionPromise = this.loadContributionData({
          country: this.country,
          targetVariable: this.targetvariable,
          targetPeriod: this.targetperiod,
          model: this.model,
        });
        const outputPromise = this.loadOutputData({
          country: this.country,
          targetVariable: this.targetvariable,
          targetPeriod: this.targetperiod,
          model: this.model,
          indicator: this.indicator,
        });
        await Promise.all([impactPromise, contributionPromise, outputPromise]);

        // Check again after async operations
        if (this.isDestroyed) {
          return;
        }

        this.$emit("activecategorieschange", this.activeCategories);
      } finally {
        if (!this.isDestroyed) {
          this.isLoadingData = false;
        }
      }

      // clickedDayTime is now initialized inside the chart's load event, which is
      // the only moment guaranteed to have both the chart live and getOutputData non-empty.
    },
  },
};
</script>

<style lang="scss">
.bar-active {
  opacity: 1;
}

.bar-inactive {
  opacity: 0.4;
}

.forecast-tab.is-inactive {
  background-color: #2d2d2d;
  color: #e0e0e0;
  border-color: #4a4a4a;

  &:hover {
    background-color: #3a3a3a;
    border-color: #5a5a5a;
  }
}

.forecast-history-chart {
  height: 850px;
}

.forecast-history-loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

.forecast-tabs-mobile {
  display: none;
  margin-bottom: 0.75rem;
}

.forecast-tab-select {
  .v-field__append-inner {
    display: flex !important;
    opacity: 1 !important;
  }
}

@media screen and (max-width: 768px) {
  .forecast-tabs-desktop {
    display: none !important;
  }

  .forecast-tabs-mobile {
    display: block;
  }

  .forecast-history-chart {
    height: 600px;
  }

  .forecast-history-column {
    padding: 0;
  }
}
</style>
